import { useState } from "react";
import { useFormik } from "formik";
import * as yup from "yup";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useDispatch } from "react-redux";
import { FormHelperText } from "@mui/material";
import PhoneInput from "react-phone-input-2";
import BaseButton from "../../components/ui/BaseButton";
import BaseModal from "../../components/ui/BaseModal";
import BaseTextField from "../../components/ui/BaseTextField";
import PasswordChecker from "../../components/ui/PasswordChecker";
import { createLinkedEngineer } from "../../services/api/freelanceCompany";
import { showToast } from "../../store/toast/toastSlice";

const validationSchema = yup.object({
  firstName: yup
    .string()
    .matches(/^[A-Za-z\s]+$/, "First name must only contain alphabets")
    .required("First name is required"),
  lastName: yup
    .string()
    .matches(/^[A-Za-z\s]+$/, "Last name must only contain alphabets")
    .required("Last name is required"),
  phoneNumber: yup.string().required("Phone number is required"),
  email: yup.string().email("Enter a valid email").required("Email is required"),
  password: yup
    .string()
    .required("Password is required")
    .min(8, "Password should be at least 8 characters long")
    .max(20, "Password should be at most 20 characters long")
    .test("has-uppercase", "Password must contain at least one uppercase letter", (v) =>
      /[A-Z]/.test(v),
    )
    .test("has-lowercase", "Password must contain at least one lowercase letter", (v) =>
      /[a-z]/.test(v),
    )
    .test("has-number", "Password must contain at least one number", (v) => /[0-9]/.test(v))
    .test("has-symbol", "Password must contain at least one symbol", (v) => /[!@#$%^&*]/.test(v)),
});

const INITIAL_VALUES = {
  firstName: "",
  lastName: "",
  phoneNumber: "",
  email: "",
  password: "",
};

const CreateEngineerModal = ({ open, onClose }) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const [isShowPassword, setIsShowPassword] = useState(false);

  const { mutateAsync: createEngineerMutation, isPending: isSubmitting } = useMutation({
    mutationFn: createLinkedEngineer,
  });

  const formik = useFormik({
    initialValues: INITIAL_VALUES,
    validationSchema,
    onSubmit: handleSubmit,
    validateOnChange: true,
    validateOnBlur: true,
  });

  async function handleSubmit(values) {
    try {
      await createEngineerMutation({
        firstName: values.firstName.trim(),
        lastName: values.lastName.trim(),
        email: values.email.trim().toLowerCase(),
        phoneNumber: values.phoneNumber.toString(),
        password: values.password,
      });
      dispatch(
        showToast({
          message: "Engineer account created and linked to your company",
          type: "success",
        }),
      );
      queryClient.invalidateQueries({ queryKey: ["freelanceCompanyMembers"] });
      handleClose();
    } catch (error) {
      dispatch(
        showToast({
          message: error?.response?.data?.message || "Failed to create engineer",
          type: "error",
        }),
      );
    }
  }

  const handleClose = () => {
    formik.resetForm();
    setIsShowPassword(false);
    onClose();
  };

  return (
    <form onSubmit={formik.handleSubmit}>
      <BaseModal
        open={open}
        close={handleClose}
        className="-top-[200px] overflow-auto"
        style={{ width: "1000px" }}
      >
        <div className="px-8 py-5">
          <h6 className="text-dark font-medium border-b pb-1">Add Engineer</h6>
        </div>

        <div className="px-8 py-4 grid grid-cols-3 gap-x-6 gap-y-4">
          {/* First Name */}
          <BaseTextField
            height="60px"
            label={
              <span className="text-[#525354]">
                First Name <span className="text-red-500">*</span>
              </span>
            }
            InputLabelProps={{ shrink: true }}
            labelShrink={true}
            fullWidth
            id="firstName"
            name="firstName"
            placeholder="William"
            value={formik.values.firstName}
            onChange={(e) => {
              const value = e.target.value;
              if (/^[A-Za-z\s]*$/.test(value)) formik.handleChange(e);
            }}
            onBlur={formik.handleBlur}
            error={formik.touched.firstName && Boolean(formik.errors.firstName)}
            helperText={formik.touched.firstName && formik.errors.firstName}
          />

          {/* Last Name */}
          <BaseTextField
            height="60px"
            label={
              <span className="text-[#525354]">
                Last Name <span className="text-red-500">*</span>
              </span>
            }
            InputLabelProps={{ shrink: true }}
            labelShrink={true}
            fullWidth
            id="lastName"
            name="lastName"
            placeholder="Clark"
            value={formik.values.lastName}
            onChange={(e) => {
              const value = e.target.value;
              if (/^[A-Za-z\s]*$/.test(value)) formik.handleChange(e);
            }}
            onBlur={formik.handleBlur}
            error={formik.touched.lastName && Boolean(formik.errors.lastName)}
            helperText={formik.touched.lastName && formik.errors.lastName}
          />

          {/* Phone Number */}
          <div className="relative">
            <label
              className="absolute z-10 bg-white px-1"
              style={{
                top: "-6px",
                left: "12px",
                fontSize: "12px",
                fontFamily: "Poppins",
                color: "#686868",
                fontWeight: 400,
                lineHeight: 1,
              }}
            >
              Phone Number <span className="text-red-500">*</span>
            </label>
            <PhoneInput
              specialLabel=""
              inputClass={`
                !w-full !font-normal !bg-white !border !rounded-lg
                ${
                  formik.touched.phoneNumber && formik.errors.phoneNumber
                    ? "!border-red-600"
                    : "!border-gray-300 focus:!border-[#0F40AE]"
                }
                focus:!outline-none focus:!ring-1 focus:!ring-[#0F40AE]
              `}
              buttonClass="!border-none !bg-transparent"
              inputStyle={{
                height: "60px",
                width: "100%",
                borderRadius: "8px",
                fontSize: "13px",
                fontFamily: "Poppins",
                color: "#686868",
                fontWeight: 300,
                ...(formik.touched.phoneNumber &&
                  formik.errors.phoneNumber && { border: "1px solid #d32f2f" }),
              }}
              country="au"
              id="phoneNumber"
              name="phoneNumber"
              value={formik.values.phoneNumber}
              onChange={(value) => formik.setFieldValue("phoneNumber", value)}
              onBlur={() => formik.setFieldTouched("phoneNumber", true)}
            />
            {formik.touched.phoneNumber && formik.errors.phoneNumber && (
              <FormHelperText error>{formik.errors.phoneNumber}</FormHelperText>
            )}
          </div>

          {/* Email */}
          <BaseTextField
            height="60px"
            label={
              <span className="text-[#525354]">
                Email <span className="text-red-500">*</span>
              </span>
            }
            InputLabelProps={{ shrink: true }}
            labelShrink={true}
            fullWidth
            id="email"
            name="email"
            placeholder="engineer@example.com"
            value={formik.values.email}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.email && Boolean(formik.errors.email)}
            helperText={formik.touched.email && formik.errors.email}
          />

          {/* Password */}
          <div className="col-span-2">
            <BaseTextField
              endAdornment={
                <i
                  className={`fa-solid ${isShowPassword ? "fa-eye" : "fa-eye-slash"} text-base cursor-pointer`}
                  onClick={() => setIsShowPassword((v) => !v)}
                />
              }
              height="60px"
              fullWidth
              id="password"
              name="password"
              placeholder="Hello@321"
              label={
                <span className="text-[#525354]">
                  Password <span className="text-red-500">*</span>
                </span>
              }
              InputLabelProps={{ shrink: true }}
              labelShrink={true}
              type={isShowPassword ? "text" : "password"}
              value={formik.values.password}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.password && Boolean(formik.errors.password)}
              helperText={formik.touched.password && formik.errors.password}
              sx={{
                "& input::-ms-reveal": { display: "none" },
                "& input::-ms-clear": { display: "none" },
              }}
            />
          </div>

          {/* Password strength checker */}
          <div className="col-span-3">
            <PasswordChecker password={formik.values.password} />
          </div>
        </div>

        <div
          className="d-flex content-center bg-white py-6 mt-6"
          style={{ boxShadow: "0px 0px 14px 0px rgba(0, 0, 0, 0.07)" }}
        >
          <BaseButton
            className="px-[28px] py-[5px] text-primary hover:!text-white"
            variant="outlined"
            color="primary"
            style={{ marginRight: "16px" }}
            onClick={handleClose}
          >
            Cancel
          </BaseButton>
          <BaseButton
            disabled={Object.keys(formik.errors).length > 0}
            isLoading={isSubmitting}
            loaderSize="22px"
            color="primary"
            className="py-[5px] px-[20px] min-w-32"
            type="submit"
            onClick={formik.handleSubmit}
          >
            Create
          </BaseButton>
        </div>
      </BaseModal>
    </form>
  );
};

export default CreateEngineerModal;
